import { useEffect, useRef } from 'react';

const SubtleFloatingParticles = ({ className = '', count = 50 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const particles = [];
    let animationFrameId;

    // Configurar tamaño del canvas
    const resizeCanvas = () => {
      const rect = canvas.parentElement?.getBoundingClientRect() || canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resizeCanvas();
    
    const resizeObserver = new ResizeObserver(() => {
      resizeCanvas();
    });
    if (canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    
    window.addEventListener('resize', resizeCanvas);

    // Crear partículas sutiles
    class Particle {
      constructor() {
        this.reset();
      }

      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.baseSize = Math.random() * 2 + 1;
        this.size = this.baseSize;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.3 + 0.2; // Muy sutil
        this.baseHue = Math.random() * 40 + 200; // Azules suaves
        this.hue = this.baseHue;
      }

      update() {
        // Movimiento suave
        this.x += this.speedX;
        this.y += this.speedY;

        // Fricción suave
        this.speedX *= 0.998;
        this.speedY *= 0.998;

        // Rebote en los bordes
        if (this.x < 0 || this.x > canvas.width) {
          this.speedX *= -0.8;
          this.x = Math.max(0, Math.min(canvas.width, this.x));
        }
        if (this.y < 0 || this.y > canvas.height) {
          this.speedY *= -0.8;
          this.y = Math.max(0, Math.min(canvas.height, this.y));
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 50%, 70%, ${this.opacity})`;
        ctx.fill();
      }
    }

    // Inicializar partículas
    for (let i = 0; i < count; i++) {
      particles.push(new Particle());
    }

    // Función de animación
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Dibujar conexiones sutiles entre partículas cercanas
      particles.forEach((particle, i) => {
        particle.update();
        
        // Conectar partículas cercanas (muy sutiles)
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 120) {
            ctx.beginPath();
            const connectionOpacity = 0.15 * (1 - distance / 120) * Math.min(particle.opacity, otherParticle.opacity);
            ctx.strokeStyle = `rgba(59, 130, 246, ${connectionOpacity})`;
            ctx.lineWidth = 1;
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
          }
        });
        
        particle.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrameId);
    };
  }, [count]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 ${className}`}
      style={{ 
        zIndex: 0, 
        width: '100%', 
        height: '100%',
        pointerEvents: 'none',
        opacity: 0.4 // Opacidad muy sutil
      }}
    />
  );
};

export default SubtleFloatingParticles;






